import {AbstractRepository} from "../repository";
import {ClientContact, PermissionSet} from "./network-repository";

export interface ClientContactDetails {
    id: number
    sub: string|null
    name: string
    preferred_username: string|null
    gender: string
    given_name: string
    family_name: string
    email: string|null
    enabled: boolean
    role_name: string
}

export class ClientContactDetails {
  constructor(
    public id: number,
    public sub: string|null,
    public name: string,
    public preferred_username: string|null,
    public gender: string,
    public given_name: string,
    public family_name: string,
    public email: string|null,
    public enabled: boolean,
    public role_name: string
  ) {
  }

  public static fromJson(json: any): ClientContactDetails {
    return new ClientContactDetails(
      json['id'],
      json['sub'],
      json['name'],
      json['preferred_username'],
      json['gender'],
      json['given_name'],
      json['family_name'],
      json['email'],
      json['enabled'],
      json['role_name']
    )
  }
}

export class ClientContRelationType {
  constructor(
    public id: number,
    public name: string,
    public category: string,
  ) {
  }

  public static fromJson(json: any): ClientContRelationType {
    return new ClientContRelationType(
      json['id'],
      json['name'],
      json['category'],
    )
  }
}

export class ClientContactPermissions {
  constructor(
    public clientId: number,
    public canAccessAgenda: boolean,
    public canAccessContacts: boolean,
    public canAccessDocuments: boolean,
    public canAccessTasks: boolean
  ) {
  }

  public static fromJson(json: any): ClientContactPermissions {
    return new ClientContactPermissions(
      json['client_id'],
      json['can_access_agenda'],
      json['can_access_contacts'],
      json['can_access_documents'],
      json['can_access_tasks']
    )
  }
}

export default class ClientContactsRepository extends AbstractRepository {
    /**
     * List all clients
     */
    public async all(): Promise<ClientContactDetails[]> {
      const json = await this.request<{ data: object[] }>('GET', 'client_contacts')
      return json.data.map((item) => ClientContactDetails.fromJson(item))
    }

    public async add(firstName: string, lastName: string, email: string, relationType: number, gender: string) {
      const json = await this.request<{ data: object }>('POST', 'client_contact', {
        first_name: firstName,
        last_name: lastName,
        email: email,
        relation_type_id: relationType,
        gender: gender
      })
      return ClientContactDetails.fromJson(json.data)
    }

    public async clientContactPermissions(clientContactId: string): Promise<ClientContactPermissions[]> {
      const json = await this.request<{ data: object[] }>('GET', `client_contact/${clientContactId}/permissions`)
      return json.data.map((item) => ClientContactPermissions.fromJson(item))
    }

    public async updateClientContactPermissions(clientContactId: string, clientId: number, canAccessAgenda: boolean, canAccessContacts: boolean, canAccessDocuments: boolean, canAccessTasks: boolean ): Promise<void> {
      return this.request('PUT', `client_contact/${clientContactId}/permissions`, {
        client_id: clientId,
        can_access_agenda: canAccessAgenda,
        can_access_contacts: canAccessContacts,
        can_access_documents: canAccessDocuments,
        can_access_tasks: canAccessTasks
      })
    }

  public updateMemberPermissions(memberSubject: string, permissions: PermissionSet): Promise<void> {
    return this.request('PUT', `client_contact/${memberSubject}/permissions`, permissions)
  }

    public async delete(clientContactId: string): Promise<void> {
      await this.request('DELETE', `client_contact/${clientContactId}`)
    }

    public async allTypes(): Promise<ClientContRelationType[]> {
      const json = await this.request<{ data: object[] }>('GET', 'client_contact_relation_types')
      return json.data.map((item) => ClientContRelationType.fromJson(item))
    }

    public async myPendingInvites() {
      const json = await this.request<{ data: number }>('GET', 'my_pending_client_contacts')
      return json.data
    }
}
